import {color, init_tooltips, number_format, on} from './util';
import * as Highcharts from "highcharts";
import DataTable, {Order} from "datatables.net-bs5";
import { groupColor } from './constants';
import { initModal, openModal } from './home/modal';
import { initRunsModal, openRunsModal } from './home/runs-modal';
import { openStairmasterModal } from './home/stairmaster-modal';
import { initGymModal, openGymModal } from './home/gym-modal';
import { initMoodModal, openMoodModal } from './home/mood-modal';
import { initCindyModal, openCindyModal } from './home/cindy-modal';
import { initSortable } from './home/sortable';
import { initCustomize } from './home/customize';
import { wireNullAwareHover } from './utils/emptyAwareTooltip';
import { initImportModal } from './import-modal';
import { getChartTheme } from './utils/chartTheme';
import { updateChartTotals, estimateStyleFor } from './home/shared';
import { snapToDayMs } from './home/day-snap';
import { initDateNav } from './home/date-nav';
import { buildTooltipFor } from './home/tooltips';

// Phase 279 D-09/D-12: the ONE list of GRAPH_KEYS-backed chart ids that are both hover-wired
// (wireNullAwareHover, both the `load` and `render` chart events below) and tooltip.shared-enabled.
// All 20 HomeModel::GRAPH_KEYS keys, copied from the const (never hand-retyped). A future chart
// key added to GRAPH_KEYS without a corresponding entry here fails tools/probe-279-tooltip-keys.php.
const HOME_TOOLTIP_KEYS = [
    'reading', 'alc', 'coffee', 'weed', 'weight', 'waist', 'transactions', 'km', 'stairmaster',
    'oura_steps', 'gym', 'cindy', 'trades', 'trades_gold', 'snap', 'mood', 'bmi', 'shows_movies',
    'music', 'oura_sleep',
];
declare const data_array : Record<string, any>;
declare const dates_array : string[];
declare const source_data : any[];
declare const source_language_data : any[];
declare const heatmap_data : any;

export default class {
    static init = () => {
        init();
        charts();
        tables();
        source();
        source_language();
        months();
        heatmaps();
        initModal();
        initRunsModal();
        initGymModal();
        initMoodModal();
        initCindyModal();
        initSortable();
        initCustomize();
        initDateNav();
        bindCardClickListener();
        initImportModal({
            onSuccess: (_result) => {
                // index.ts runs home.init() on EVERY page; only the Home host owns the
                // in-place transactions-chart redraw. Guard on the home chart's presence so
                // this never fires the relative ?do=getTransactionsSeries on a non-Home
                // presenter (e.g. on /transactions → TransactionsPresenter → 403). On the home
                // page the relative signal correctly resolves to HomePresenter.
                if (!findChartById('chart_transactions')) return;
                // Phase 183 (UX-02, D-08/D-10): show skeleton shimmer around the post-import
                // chart-reload fetch, hide once the redraw completes. Toggle the OUTER container
                // by id (#home-chart-skeleton), never the bare .ksat-skeleton-group class — the
                // shared ask-modal renders a second copy of that class on every show_header page
                // (WR-02/WR-03).
                const chartSkel = document.getElementById('home-chart-skeleton');
                if (chartSkel) chartSkel.hidden = false;
                fetch('?do=getTransactionsSeries', { method: 'POST' })
                    .then(r => r.json())
                    .then((data: any) => {
                        if (data.success) redrawTransactionsChart(data.series);
                    })
                    .catch(() => { /* silent — chart stays stale on network error */ })
                    .finally(() => { if (chartSkel) chartSkel.hidden = true; });
            }
        });
    }
}

// ── Highcharts helpers for post-import series redraw ────────────────────────

export function findChartById(chartId: string): Highcharts.Chart | undefined {
    //@ts-ignore
    return (Highcharts.charts as any[]).find((c: any) => c?.renderTo?.id === chartId);
}

function redrawTransactionsChart(seriesData: { name: string[]; data: any[][]; totals?: Array<{ name: string; value: string }> }): void {
    const chart = findChartById('chart_transactions');
    if (!chart) return;

    // Remove ALL existing series (series count may change after import — Pitfall 6)
    while (chart.series.length > 0) {
        chart.series[0].remove(false);
    }

    // Add new series — never pass undefined options (Highcharts landmine)
    seriesData.name.forEach((name: string, i: number) => {
        const color = groupColor(name);
        chart.addSeries({
            type: 'column',
            name,
            data: seriesData.data[i],
            ...(color !== undefined ? { color } : {}),
            connectNulls: true,
        } as Highcharts.SeriesOptionsType, false);
    });

    chart.redraw();

    // D-12: getTransactionSeriesData already returns a flat totals list — the titlebar just
    // never read it. No backend change needed; swap it in alongside the series redraw so the
    // transactions titlebar no longer goes stale after a CSV import.
    updateChartTotals('transactions', seriesData.totals);
}

function init() {
    on('change', 'select[name="year"]', (element : HTMLInputElement) => {
        location.href = '?year=' + element.value;
    });
    on('change','input[name="from"],input[name="to"]',(element : HTMLInputElement) => {
        let from = element.name === 'from' ? element.value : document.querySelector<HTMLInputElement>('input[name="from"]')?.value;
        let to = element.name === 'to' ? element.value : document.querySelector<HTMLInputElement>('input[name="to"]')?.value;
        location.href = '?from=' + from + '&to=' + to;
    });
    on('change', '.js-yoy-toggle', (element: HTMLInputElement) => {
        location.href = '?yoy=' + ((element as HTMLInputElement).checked ? '1' : '0');
    });
}

function applyEstimateStyle(
    points: Highcharts.PointOptionsObject[],
    chartKey: string,
    seriesIndex = 0,
): Highcharts.PointOptionsObject[] {
    return points.map(pt => {
        if (typeof pt !== 'object' || pt === null) return pt;
        const isEstimate = (pt as any).custom?.estimate === 1;
        if (!isEstimate) return pt;
        return {
            ...pt,
            color: estimateStyleFor(chartKey, seriesIndex),
            borderWidth: 1,
            dashStyle: 'ShortDash' as Highcharts.DashStyleValue,
        };
    });
}

function applyOuraStyle(
    points: Highcharts.PointOptionsObject[],
): Highcharts.PointOptionsObject[] {
    return points.map(pt => {
        if (typeof pt !== 'object' || pt === null) return pt;
        if ((pt as any).hasOura !== true) return pt;
        return {
            ...pt,
            marker: {
                enabled: true,
                symbol: 'circle',
                radius: 5,
                fillColor: 'var(--ksat-accent)',
                lineColor: 'var(--ksat-accent)',
                lineWidth: 2,
            },
        };
    });
}

/** Extract the y-value from a raw prior_data entry (object with .y, or raw number/null). */
function extractPriorY(raw: any): number | null {
    if (raw == null) return null;
    if (typeof raw === 'object') return (raw as any).y ?? null;
    return raw as number | null;
}

/**
 * Linearly interpolate a value at ptIdx from the nearest non-null neighbours.
 * Works with both flat number|null arrays (current-year) and object-with-y arrays (prior alc).
 * Returns null when neighbours cannot be found on both sides (gap at edge or all-null).
 */
function interpolateAt(arr: any[], ptIdx: number): number | null {
    let loIdx = -1, hiIdx = -1;
    for (let i = ptIdx - 1; i >= 0; i--) {
        const v = extractPriorY(arr[i]);
        if (v !== null) { loIdx = i; break; }
    }
    for (let i = ptIdx + 1; i < arr.length; i++) {
        const v = extractPriorY(arr[i]);
        if (v !== null) { hiIdx = i; break; }
    }
    if (loIdx === -1 || hiIdx === -1) return null;
    const lo = extractPriorY(arr[loIdx]) as number;
    const hi = extractPriorY(arr[hiIdx]) as number;
    const t = (ptIdx - loIdx) / (hiIdx - loIdx);
    return lo + (hi - lo) * t;
}

function buildYoyExtraRows(
    priorDataArr: any[] | undefined,
    chartId: string,
    currentDataArr?: any[],
    suffixFmt?: (y: number) => string,
): (ctx: Highcharts.Point) => string[] {
    if (!priorDataArr) return () => [];
    const isLineSeries = chartId === 'weight' || chartId === 'waist';
    return (ctx) => {
        const ptIdx = ((ctx as any).point as any)?.index as number | undefined;
        if (ptIdx == null) return [];

        // --- Current value ---
        let currentY = ctx.y as number | null;
        let isCurrentEstimate = false;
        if (currentY === null && isLineSeries && currentDataArr) {
            const interpolated = interpolateAt(currentDataArr, ptIdx);
            if (interpolated !== null) {
                currentY = parseFloat(interpolated.toFixed(1));
                isCurrentEstimate = true;
            }
        }

        // --- Prior value ---
        const priorRaw = priorDataArr[ptIdx];
        let priorY: number | null = extractPriorY(priorRaw);
        let isPriorEstimate = false;
        if (priorY === null && isLineSeries) {
            const interpolated = interpolateAt(priorDataArr, ptIdx);
            if (interpolated !== null) {
                priorY = parseFloat(interpolated.toFixed(1));
                isPriorEstimate = true;
            }
        }

        // alc is stored negative for inverted display — compute on magnitudes
        const cur = chartId === 'alc' && currentY != null ? Math.abs(currentY) : currentY;
        const pri = chartId === 'alc' && priorY   != null ? Math.abs(priorY)   : priorY;

        const estMark = ' <span style="color:#999;font-style:italic;">(est.)</span>';
        const curFmt  = cur != null
            ? `${suffixFmt ? suffixFmt(cur) : cur}${isCurrentEstimate ? estMark : ''}`
            : '—';
        const priFmt  = pri != null
            ? `${suffixFmt ? suffixFmt(pri) : pri}${isPriorEstimate  ? estMark : ''}`
            : '—';

        let chgFmt = '—';
        if (cur != null && pri != null && pri !== 0) {
            const pct = ((cur - pri) / pri) * 100;
            const sign = pct >= 0 ? '+' : '';
            const chgEst = (isCurrentEstimate || isPriorEstimate) ? estMark : '';
            chgFmt = `${sign}${Math.round(pct)}%${chgEst}`;
        }
        return [
            `<span style="color:#999">Current:</span> <b>${curFmt}</b><br/>`,
            `<span style="color:#999">Prior:</span> <b>${priFmt}</b><br/>`,
            `<span style="color:#999">Change:</span> <b>${chgFmt}</b><br/>`,
        ];
    };
}

const LBS_TO_KG = 0.45359237;
const CM_TO_IN  = 1 / 2.54;

function fmtDualUnit(primaryVal: number, suffix: string, chartId: string): string {
    if (chartId === 'weight') {
        return `${primaryVal}${suffix} (${(primaryVal * LBS_TO_KG).toFixed(1)} kg)`;
    }
    if (chartId === 'waist') {
        return `${primaryVal}${suffix} (${(primaryVal * CM_TO_IN).toFixed(1)} in)`;
    }
    return `${primaryVal}${suffix}`;
}

// Module-scope guard: prevents chart.events.click from firing when a data point click
// already handled the event. Shared across every renderChart() instance (initial load AND
// the D-15 un-hide re-render) since Highcharts point/chart events are independent callbacks
// (not DOM events) — e.stopPropagation() does NOT work here. P-15 extends this to a third
// participant, the delegated card-click listener below: the chart-level handler (edited for
// HOME-03 rounding) now also SETS this flag before dispatching, so a click that already
// reached Highcharts doesn't also open the modal a second time when the same native click
// event bubbles up to the document-level listener.
let _pointClickInProgress = false;

// P-15: the ONE shared editable-chart allowlist. Both updateValue() (populated-card
// point/chart click dispatch, below) and bindCardClickListener() (delegated empty-card
// click, below) read this same array — a second hard-coded copy would let the two drift.
// Exported (Phase 262 plan 262-10) so customize.ts's buildChartWrapper() can strip the
// click-hint copy for a non-editable key on the client-cloned empty-state path, without
// introducing a third hard-coded copy of the list.
export const EDITABLE_CHART_IDS = ['alc', 'weight', 'waist', 'gym', 'km', 'stairmaster', 'snap', 'mood', 'coffee', 'weed', 'cindy'];

// Idempotency guard (feedback_shared_component_every_page_init_double_bind): a repeat
// home.init() run (this class's static init runs on every page load) must not install a
// second document-level click listener.
let _cardClickBound = false;

/**
 * P-14/P-15/P-16 (MODAL-01): one delegated click listener that makes every EDITABLE chart
 * card clickable whether or not it has a Highcharts instance. Empty-state cards render
 * `.chart.chart-empty` with no `chart_<id>` Highcharts render target, so `openModal()`'s only
 * pre-existing callers — both inside `renderChart()`'s Highcharts options — could never fire
 * for them; 9 of 10 editable graphs were a click dead end when empty.
 *
 * A plain scoped `document.addEventListener` is used instead of the `on()` helper's dynamic
 * branch (util.ts:14-27): `on()` only hands the callback the matched ancestor element, not
 * the original event, so it cannot express the drag-handle/resize-handle exclusion below. Do
 * not "fix" this back to `on(...)` — that helper's shape does not support this case.
 */
function bindCardClickListener(): void {
    if (_cardClickBound) return;
    _cardClickBound = true;
    document.addEventListener('click', (e) => {
        // Third participant in the _pointClickInProgress guard: a populated card's Highcharts
        // point- or chart-level click handler already ran (they're attached to elements
        // inside .chart-wrapper, so they fire earlier in the same native click event's bubble
        // phase, before it reaches this document-level listener) and already dispatched.
        if (_pointClickInProgress) return;
        const target = e.target as HTMLElement | null;
        if (!target) return;
        // Exclude the drag-handle control strip (grip, totals, import/hide buttons) and the
        // resize handle — neither should open the edit modal.
        if (target.closest('.chart-drag-handle, .chart-resize-handle')) return;
        const wrapper = target.closest('.chart-wrapper[data-id]') as HTMLElement | null;
        if (!wrapper) return;
        const key = wrapper.getAttribute('data-id');
        if (!key || !EDITABLE_CHART_IDS.includes(key)) return;
        // P-16: there is no clicked point to derive a date from — always open on today, built
        // from local getters, not a UTC-shifting Date serialization, which would hand the
        // modal the wrong day on any evening before 20:00 local.
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        updateValue(key, todayStr);
    });
}

/**
 * Phase 282 gap closure (QUICK-06 / GAP 1, 282-VERIFICATION.md): the SINGLE definition of
 * "this graph gets a live Highcharts instance rather than the empty-state card". A chart with
 * an empty data window renders as a live chart only when a goal target resolved for it
 * (goal_forced) — otherwise the empty-state card still applies exactly as before.
 * `src/js/home/customize.ts` imports this (Plan 282-15) so the initial-load path and the client
 * re-render path can never drift, and `Home/default.latte` line 163's condition encodes the
 * identical predicate server-side.
 */
export function rendersAsChart(graph: any): boolean {
    return !graph.empty_in_range || !!graph.goal_forced;
}

function charts() {
    if(typeof data_array !== 'undefined') {
        for(let [id, data] of Object.entries(data_array)) {
            // Pitfall 3: show can now be true for an empty-in-range graph (show-all ON, or the
            // forced-key D-15 path) — Highcharts must never render into an empty-state card.
            if(!data.show || !rendersAsChart(data)) {
                continue;
            }
            renderChart(id, data);
        }
    }
}

/**
 * D-15 (Phase 218): builds + instantiates a single Home chart. Extracted from charts()'s
 * per-graph loop body so the initial-load loop and customize.ts's on-demand un-hide
 * re-render share ONE builder — output must be identical to the pre-refactor initial load.
 */
export function renderChart(id: string, data: any): Highcharts.Chart | undefined {
    const button_background = getComputedStyle(document.documentElement).getPropertyValue('--ksat-surface-raised').trim();

    let series = [] as Highcharts.SeriesOptionsType[];
            data.data.forEach((value : any, index : number) => {
                let seriesColor: any;
                if (id === 'transactions') {
                    seriesColor = groupColor(data.name[index]);
                } else if (id === 'alc') {
                    seriesColor = {
                        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                        stops: [
                            [0, '#E1A818'],
                            [0.1, data.colors[0]],
                            [0.9, data.colors[0]],
                            [1, '#E1A818']
                        ]
                    } as Highcharts.GradientColorObject;
                } else if (id === 'shows_movies' || id === 'music') {
                    // D-08: reuse the exact dynamic-color mechanism transactions already
                    // proved for an unbounded series set — no new color logic.
                    seriesColor = groupColor(data.name[index]);
                } else {
                    seriesColor = data.colors[index];
                }
                // trades: Gold (idx 2), snap: Score (idx 1), oura_sleep: Sleep Score (idx 4)
                // render as a spline on the 2nd yAxis (D-09)
                const isSecondaryAxisSpline = (id === 'trades' && index === 2) || (id === 'snap' && index === 1) || (id === 'oura_sleep' && index === 4);
                series.push({
                    type: isSecondaryAxisSpline ? 'spline' : data.type,
                    // Rule 1 fix: single-series charts (alc/weight/waist/mood/bmi) carry a
                    // bare string 'name' (not an array) — indexing a string with [index]
                    // returned just its first character ("W" instead of "Weight") in every
                    // tooltip. Multi-series charts keep the existing array-index lookup.
                    name: Array.isArray(data.name) ? data.name[index] : data.name,
                    data: value,
                    yAxis: isSecondaryAxisSpline ? 1 : 0,
                    // oura_sleep honours the same gap contract as oura_steps: OuraModel's
                    // getSleepStageSeries() docblock mandates connectNulls:false so missing
                    // nights render as gaps. It matters for the Sleep Score spline (index 4) —
                    // with connectNulls:true it draws a straight invented line across nights
                    // that have no Oura data at all.
                    connectNulls: (id === 'oura_steps' || id === 'oura_sleep') ? false : true,
                    color: seriesColor
                } as Highcharts.SeriesOptionsType);
            });
            // Add time overlay spline on right yAxis for km and stairmaster charts
            if ((id === 'km' || id === 'stairmaster') && data.time_data && data.time_data.length > 0) {
                series.push({
                    type: 'spline',
                    name: 'time',
                    data: data.time_data,
                    yAxis: 1,
                    connectNulls: false,
                    color: id === 'km' ? 'rgba(0,144,255,0.5)' : 'rgba(255,140,0,0.5)',
                    dashStyle: 'ShortDash',
                    marker: { enabled: false },
                    enableMouseTracking: false,
                } as Highcharts.SeriesOptionsType);
            }
            if (id === 'alc') {
                const alcSeries = series[0] as Highcharts.SeriesColumnOptions;
                alcSeries.data = applyEstimateStyle(
                    alcSeries.data as Highcharts.PointOptionsObject[],
                    id.toString()
                );
            }
            if (id === 'coffee') {
                const coffeeSeries = series[0] as Highcharts.SeriesColumnOptions;
                coffeeSeries.data = applyEstimateStyle(
                    coffeeSeries.data as Highcharts.PointOptionsObject[],
                    id.toString()
                );
            }
            if (id === 'weed') {
                // D-11: one row-level estimate flag covers the whole day — dim BOTH
                // grouped series (puffs + thc_drinks) together, not just one.
                const weedPuffsSeries = series[0] as Highcharts.SeriesColumnOptions;
                weedPuffsSeries.data = applyEstimateStyle(
                    weedPuffsSeries.data as Highcharts.PointOptionsObject[],
                    id.toString()
                );
                const weedThcSeries = series[1] as Highcharts.SeriesColumnOptions;
                weedThcSeries.data = applyEstimateStyle(
                    weedThcSeries.data as Highcharts.PointOptionsObject[],
                    id.toString(),
                    1
                );
            }
            if (id === 'km') {
                const kmSeries = series[0] as Highcharts.SeriesColumnOptions;
                kmSeries.data = applyOuraStyle(
                    kmSeries.data as Highcharts.PointOptionsObject[]
                );
            }
            const hasYoy = (data_array as any).yoy_enabled === true;
            // D-11: bmi is YoY-eligible (mood/shows_movies/music/oura_sleep opt out — see D-10).
            // Coffee's prior_data is a flat single array (mirrors alc) — handled directly here.
            if (hasYoy && data.prior_data && ['alc', 'weight', 'waist', 'bmi', 'coffee'].includes(id.toString())) {
                const priorYear = data.prior_year ?? (new Date().getFullYear() - 1);
                const priorColor = data.colors[0]; // same hue; opacity dims it
                // D-08: alc/coffee prior-year points carry custom.estimate from HomeModel now —
                // route them through the same applyEstimateStyle() used for current-year points
                // so an estimate bar reads as "same hue, dimmed" under the opacity:0.4 wrapper
                // below, not a fourth palette. Per-point color wins over the series-level
                // priorColor set here (Highcharts point color overrides series color).
                const priorData = (id === 'alc' || id === 'coffee')
                    ? applyEstimateStyle(data.prior_data as Highcharts.PointOptionsObject[], id.toString())
                    : data.prior_data;
                series.push({
                    type: data.type,
                    name: String(priorYear),
                    data: priorData,
                    opacity: 0.4,
                    color: priorColor,
                    connectNulls: id === 'weight' || id === 'waist' || id === 'bmi',
                    ...(id === 'weight' || id === 'waist' || id === 'bmi'
                        ? { dashStyle: 'Dot' as Highcharts.DashStyleValue, marker: { enabled: false } }
                        : {}),
                } as Highcharts.SeriesOptionsType);
            }
            // Open Question 1 (No Analog Found): weed's prior_data arrives as an
            // array-of-two-arrays (mirrors weed's own doubled current-year 'data' shape) —
            // no existing home graph overlays MORE than one prior-year series, so this loop
            // generalizes the single-push pattern above into one prior series per current-year
            // metric (puffs + THC-drinks), keeping the grouped (non-stacked, D-02) column type
            // so all 4 columns/day sit side-by-side. Ship the honest 2-prior-series overlay
            // first; if 320px UAT flags clutter, the fallback is thinner/ghost-outline prior
            // bars via reduced pointWidth (not implemented here — UAT decides).
            if (hasYoy && id === 'weed' && Array.isArray(data.prior_data)) {
                const priorYear = data.prior_year ?? (new Date().getFullYear() - 1);
                data.prior_data.forEach((priorSeriesData: any, i: number) => {
                    // D-06/D-08: one weed estimate scheme covers both puffs and THC-drinks
                    // prior-year series, same as the current-year weed treatment above.
                    const styledPriorSeriesData = applyEstimateStyle(
                        priorSeriesData as Highcharts.PointOptionsObject[],
                        id.toString(),
                        i
                    );
                    series.push({
                        type: data.type,
                        name: `${priorYear} ${Array.isArray(data.name) ? data.name[i] : data.name}`,
                        data: styledPriorSeriesData,
                        opacity: 0.4,
                        color: data.colors[i],
                        connectNulls: false,
                    } as Highcharts.SeriesOptionsType);
                });
            }
            let yaxis = [{
                type: id == 'reading' ? 'datetime' : 'linear',
                title: {
                    text: '',
                },
                labels: {
                    formatter: id == 'reading' ? function () {
                        //@ts-ignore
                        return diffForHumans(this.value, 2);
                    } : undefined
                },
                minorGridLineWidth: 0.1,
                gridLineWidth: 0.1,
                min: data.min,
                max: data.max
            }] as Highcharts.YAxisOptions[];
            if (id === 'bmi') {
                // D-11: WHO BMI category bands — underweight <18.5 / normal 18.5–25 /
                // overweight 25–30 / obese ≥30. No --ksat-* status tokens exist for this
                // (verified) — hardcoded low-opacity rgba matches house convention (every
                // other band/data color in this codebase is a hardcoded hex/rgba).
                yaxis[0].plotBands = [
                    { from: 0,    to: 18.5, color: 'rgba(96,165,250,0.10)' },  // underweight
                    { from: 18.5, to: 25,   color: 'rgba(34,197,94,0.10)'  },  // normal
                    { from: 25,   to: 30,   color: 'rgba(250,204,21,0.10)' },  // overweight
                    { from: 30,   color: 'rgba(239,68,68,0.10)'  },  // obese — open-ended, extends to axis max
                ] as Highcharts.YAxisPlotBandsOptions[];
            }
            // Phase 282 Plan 10 (QUICK-06, D-18/D-19/D-20): draws the goal target as a dashed
            // labelled line on the weight and km charts ONLY, and ONLY when HomeModel resolved a
            // matching goal (the payload field checked in the guard below is present). D-19's
            // entire "no goal → no line" contract lives in this falsy guard: when HomeModel
            // omitted the field (no matching goal row), the check below is undefined, this branch
            // never runs, and yaxis[0] is untouched — the chart config is byte-identical to before
            // this branch existed. No else arm, no defaulting (`?? `/`||`) of a missing value to a
            // placeholder — either would defeat the silent-absence contract. Deliberately
            // mode-blind: this does NOT check prior-year/yoy state at all, because a goal target
            // is not year-scoped (D-19) — the same single line draws in year-over-year mode.
            if ((id === 'weight' || id === 'km') && data.plotline) {
                yaxis[0].plotLines = [{
                    value: data.plotline.value,
                    // No --ksat-* status token exists for a goal-target line (verified against
                    // the bmi block's own precedent above) — a hardcoded rgba matches house
                    // convention (every other band/series color in this file is a literal).
                    color: 'rgba(239,68,68,0.6)',
                    dashStyle: 'Dash',
                    width: 1,
                    zIndex: 5,
                    label: {
                        text: data.plotline.label,
                        align: 'right',
                    },
                }] as Highcharts.YAxisPlotLinesOptions[];
                // Phase 282 gap closure round 2 (QUICK-06 / GAP 1, 282-GATE-RESULTS.md RED 1):
                // Highcharts does NOT auto-extend an axis to include a plotLines value — when the
                // target falls outside [axis.min, axis.max] it still emits a
                // <path class="highcharts-plot-line"> element but never assigns it a `d`
                // attribute, so nothing paints. Same failure mode that previously bit the bmi
                // band on this exact file.
                //
                // Two cases, two bound kinds — Axis.setTickInterval()
                // (node_modules/highcharts/esm/highcharts.src.js ~20441-20520) is why they must
                // differ:
                //   - Has-data case (weight, and any km with runs in range): axis.min/axis.max
                //     resolve from pick(hardMin, thresholdMin, dataMin) / pick(hardMax,
                //     thresholdMax, dataMax) at line ~20480-20481. softMin/softMax are only read
                //     AFTER that, inside the `if (isNumber(axis.min) && isNumber(axis.max))` gate
                //     at line ~20509, and only to WIDEN past whatever dataMin/dataMax already
                //     produced (`softMin < axis.min` / `softMax > axis.max`, lines ~20511-20519).
                //     softMin/softMax is correct here: the target is guaranteed inside the
                //     rendered range while real data can still expand the axis past it. Proven
                //     green on production for the weight chart — UNCHANGED below.
                //   - goal_forced case (km with zero series data in range — HomeModel still pushes
                //     one {x, y:null} point per date-spine day, so series[0] exists but every y is
                //     null): dataMin/dataMax are both undefined, so axis.min/axis.max resolve to
                //     undefined at line ~20480-20481, the isNumber(axis.min) && isNumber(axis.max)
                //     gate at line ~20509 is false, and softMin/softMax are never read at all — no
                //     extremes, no ticks, nothing for the plot line to anchor to. Hard min/max
                //     feed pick() AHEAD of that gate (options.min/options.max read at line
                //     ~20451-20452, consumed at ~20480-20481), so they establish extremes with
                //     zero data. There is no real data to clip, so the usual objection to hard
                //     bounds ("it clips real data") cannot apply here — HomeModel never emits a
                //     `min`/`max` payload key for any chart, so `data.min`/`data.max` on yaxis[0]
                //     above are already undefined/undefined today and these hard bounds conflict
                //     with nothing.
                // Lives inside this same guard so the no-goal path stays byte-identical by
                // construction — one guard, one contract, no second place to drift.
                // The pad below is deliberately applied to the goal_forced arm ONLY. It is
                // proportional to the target's absolute MAGNITUDE, which is the right shape for a
                // chart with no data (something has to set the scale, and the target is the only
                // number available) and the wrong shape for a chart that has data: magnitude says
                // nothing about how much headroom a series needs. Padding the soft bounds by 5% of
                // a 120 lb weight target gave softMin=114/softMax=126, which Highcharts' tick
                // rounding widened to 110-130 for a series that only varies between 119 and 121 —
                // flattening the real trend into a straight line. Operator-reported against the
                // live chart on 2026-09-07; see 282-GATE-RESULTS.md round 2.
                const pad = Math.abs(data.plotline.value) * 0.05;
                if (data.goal_forced) {
                    // goal_forced: the chart has zero series data in range — a hairline axis
                    // clamped tight around the target looks broken, so anchor to zero instead
                    // for a sane, readable zero-based axis with the target near the top.
                    yaxis[0].min = Math.min(0, data.plotline.value - pad);
                    yaxis[0].max = data.plotline.value + pad;
                } else {
                    // Has-data: the ONLY job of the soft bounds here is to guarantee the target
                    // falls inside [axis.min, axis.max] so the plot line gets a `d`. Bare
                    // value, no pad — soft bounds only ever WIDEN past dataMin/dataMax
                    // (highcharts.src.js ~20511-20519), so with the target already inside the
                    // data's own range these are inert and the axis follows the data. When the
                    // target sits outside it, the axis stretches exactly far enough to include
                    // it and no further. Breathing room is Highcharts' job, not ours: the yAxis
                    // defaults are minPadding/maxPadding 0.05 (highcharts.src.js:17965,17985),
                    // so a target at the extreme still renders inset rather than flush.
                    yaxis[0].softMax = data.plotline.value;
                    yaxis[0].softMin = data.plotline.value;
                }
            }
            if(id === 'trades') {
                yaxis.push({
                    type: 'linear',
                    title: {
                        text: '',
                    },
                    minorGridLineWidth: 0.1,
                    gridLineWidth: 0.1,
                    min: data.min,
                    max: data.max,
                    //opposite: true
                });
            }
            if (id === 'snap') {
                // Cumulative-score axis (large values) on the right; bars (daily gain) use the left axis
                yaxis.push({
                    type: 'linear',
                    title: { text: '' },
                    minorGridLineWidth: 0,
                    gridLineWidth: 0,
                    opposite: true,
                });
            }
            if (id === 'oura_sleep') {
                // D-09: Sleep Score (0–100) renders as a spline on its own opposite axis;
                // the 4 stage bands stack on the primary axis — mirrors snap's dual-axis shape.
                yaxis.push({
                    type: 'linear',
                    title: { text: '' },
                    minorGridLineWidth: 0,
                    gridLineWidth: 0,
                    opposite: true,
                });
            }
            if (id === 'km' || id === 'stairmaster') {
                yaxis.push({
                    type: 'linear',
                    title: {
                        text: '',
                    },
                    labels: {
                        formatter: function(this: any) {
                            return diffForHumans(this.value * 1000, 1);
                        }
                    },
                    minorGridLineWidth: 0,
                    gridLineWidth: 0,
                    opposite: true,
                });
            }
            const yoyExtraRows = buildYoyExtraRows(
                (data_array as any).yoy_enabled === true ? data.prior_data : undefined,
                id.toString(),
                (data_array as any).yoy_enabled === true ? data.data[0] : undefined,
                (id === 'weight' || id === 'waist')
                    ? (y: number) => fmtDualUnit(y, (data.suffix ?? '') as string, id.toString())
                    : undefined,
            );
            const hcChart = Highcharts.chart("chart_" + id.toString(), Highcharts.merge(getChartTheme(), {
                series: series,
                chart: {
                    // Phase 178 (D-07): transparent so the elevated .chart-wrapper surface shows through.
                    backgroundColor: 'transparent',
                    // Phase 218 (D-09): height literal removed — the chart sizes to its `.chart`
                    // container, which carries `height: var(--chart-height)` (seeded server-side,
                    // JS-overwritten live by customize.ts's drag-resize). Do NOT reintroduce a JS
                    // height literal here (UI-SPEC anti-pattern #8) — the mobile 540px breakpoint's
                    // explicit `height: 220` in index.ts's Highcharts responsive rule is untouched.
                    zooming: {
                        type: 'x',
                        resetButton: {
                            position: {
                                align: "right",
                                x: 0,
                                y: -30
                            },
                            theme: {
                                fill: button_background,
                                stroke: "none",
                                style: {
                                    backgroundColor: button_background
                                },
                                states: {
                                    hover: {
                                        fill: button_background,
                                        style: {
                                            backgroundColor: button_background
                                        }
                                    }
                                }
                            }
                        }
                    },
                    events: {
                        click: function (e) {
                            // Skip if a point click already handled this event
                            if (_pointClickInProgress) return;
                            const rawMs = this.xAxis[0].toValue(e.chartX);
                            // D-14 / P-05: snap through the shared home/day-snap.ts module — the
                            // one sanctioned rounding for HOME-03. pointStart (this series' own,
                            // merged-in from plotOptions) is the alignment origin because it
                            // carries the series' actual local-midnight offset, so the result
                            // stays correct across a DST transition.
                            const snappedMs = snapToDayMs(rawMs, (this.series[0]?.options as any)?.pointStart as number || 0);
                            let date = Highcharts.dateFormat('%Y-%m-%d', snappedMs);
                            // P-15: set the guard so the delegated card-click listener
                            // (bindCardClickListener, module scope above) bails on this same
                            // click when the native event finishes bubbling to document.
                            _pointClickInProgress = true;
                            updateValue(id.toString(), date);
                            setTimeout(() => { _pointClickInProgress = false; }, 0);
                        },
                        load: function () {
                            // Manually drive tooltip on null x-positions for empty-aware charts —
                            // Highcharts excludes null points from the kdTree so native hover skips them.
                            // Phase 279 D-09/D-12: all 20 GRAPH_KEYS via the shared HOME_TOOLTIP_KEYS
                            // constant — every chart with an empty-aware formatter must also be
                            // hover-wired, or its null-x-position dates never trigger a tooltip at all.
                            if (HOME_TOOLTIP_KEYS.includes(id.toString())) {
                                wireNullAwareHover(this);
                            }
                        },
                        render: function () {
                            // D-04: re-apply null-aware hover patch after any chart.update()
                            // (including responsive breakpoint crossing at 540px). Idempotency
                            // guard in wireNullAwareHover (_nullAwarePatched sentinel) prevents
                            // double-wrapping on repeated render events.
                            if (HOME_TOOLTIP_KEYS.includes(id.toString())) {
                                wireNullAwareHover(this);
                            }
                        },
                    }
                },
                title: {
                    text: data.title,
                    // align: 'left',
                    style: {
                        fontSize: '14px'
                    }
                },
                legend: {
                    // music/shows_movies are unbounded by design (D-07: no top-N cap) — prod
                    // carries 1,079 distinct artists and 286 titles all-time, so on year=all the
                    // default legend would render a thousand-plus entries and swamp the card.
                    // They borrowed the transactions series pattern, but transactions is bounded
                    // at ~25 groups and is the only id given explicit legend layout. The shared
                    // tooltip already names every band, so the legend adds nothing here.
                    enabled: (series.length > 1 && id !== 'km' && id !== 'stairmaster'
                              && id !== 'music' && id !== 'shows_movies') || id === 'transactions',
                    ...(id === 'transactions' ? {
                        layout: 'horizontal' as const,
                        align: 'center' as const,
                        verticalAlign: 'bottom' as const,
                    } : {}),
                },
                xAxis: {
                    type: 'datetime',
                    labels: {
                        overflow: 'justify',
                    },
                    plotBands: generateWeekendPlotBands(dates_array),
                    crosshair: {
                        dashStyle: 'Dash',
                        color: getComputedStyle(document.documentElement).getPropertyValue('--ksat-border').trim(),
                        width: 1,
                        snap: true,
                        zIndex: 3,
                    },
                },
                // @ts-ignore
                yAxis: yaxis,
                tooltip: {
                    valueSuffix: data.suffix !== undefined ? data.suffix : '',
                    xDateFormat: '%b %e, %Y',
                    // Phase 279 D-09/D-12: the same HOME_TOOLTIP_KEYS constant that drives the
                    // load/render hover wiring above -- all 20 GRAPH_KEYS entries need
                    // `shared: true` for `this.points` to populate at a null x-position in ANY
                    // formatter mode (see src/js/utils/emptyAwareTooltip.ts docblock).
                    shared: HOME_TOOLTIP_KEYS.includes(id.toString()),
                    formatter: buildTooltipFor(id.toString(), data, yoyExtraRows),
                },
                plotOptions: {
                    spline: {
                        lineWidth: 4,
                        states: {
                            hover: {
                                lineWidth: 5
                            }
                        },
                        marker: {
                            enabled: false
                        },
                        pointInterval: 86400000, // one day
                        pointStart: Date.parse(dates_array[0]),
                    },
                    column: {
                        // D-02: weed's two grouped series (puffs/thc_drinks) must render
                        // side-by-side UNCONDITIONALLY, not gated on yoy like alc's narrow case.
                        stacking: ((id === 'alc' && (data_array as any).yoy_enabled === true) || id === 'weed') ? undefined : 'normal',
                        borderWidth: 0,
                        states: {
                            hover: {
                                borderWidth: 1,
                            }
                        },
                        pointInterval: 86400000, // one day
                        pointStart: Date.parse(dates_array[0]),
                    },
                    series: {
                        cursor: EDITABLE_CHART_IDS.includes(id.toString()) ? 'pointer' : undefined,
                        point: {
                            events: {
                                click: function () {
                                    // Set guard flag so chart.events.click skips on this tick
                                    _pointClickInProgress = true;
                                    let date = Highcharts.dateFormat('%Y-%m-%d', this.x);
                                    let value = this.y as number;
                                    updateValue(id.toString(), date, value, this.series.index);
                                    // Reset after current event loop tick
                                    setTimeout(() => { _pointClickInProgress = false; }, 0);
                                }
                            }
                        }
                    }
                },
                credits: {
                    enabled: false
                }
            } as any));
    return hcChart;
}

function updateValue(chart : string, date : string, value : number | undefined = undefined, seriesIndex : number = 0) {
        if (!EDITABLE_CHART_IDS.includes(chart)) return;
        if (chart === 'km') {
            openRunsModal(date);
            return;
        }
        if (chart === 'stairmaster') {
            openStairmasterModal(date);
            return;
        }
        if (chart === 'gym') {
            openGymModal(date);
            return;
        }
        if (chart === 'mood') {
            openMoodModal(date);
            return;
        }
        if (chart === 'cindy') {
            openCindyModal(date);
            return;
        }
        openModal(chart, date);
    }

    function generateWeekendPlotBands(dates: string[]): Highcharts.XAxisPlotBandsOptions[] {
        if (!dates || dates.length === 0) {
            return [];
        }

        const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];
        let currentDate = new Date(dates[0].substring(0, 10) + 'T00:00:00');
        const endDate = new Date(dates[dates.length - 1].substring(0, 10) + 'T00:00:00');

        // Advance to first Saturday (day 6)
        while (currentDate.getDay() !== 6) {
            currentDate.setDate(currentDate.getDate() + 1);
            if (currentDate > endDate) {
                return plotBands;
            }
        }

        while (currentDate <= endDate) {
            let weekendStart = new Date(currentDate);
            weekendStart.setHours(-8, 0, 0, 0);
            let weekendEnd = new Date(currentDate);
            weekendEnd.setDate(weekendEnd.getDate() + 1);
            weekendEnd.setHours(8, 0, 0, 0);

            plotBands.push({
                from: weekendStart.getTime(),
                to: weekendEnd.getTime(),
                color: 'rgba(128, 128, 128, 0.1)',
                label: {
                    text: '',
                    style: {
                        color: '#999'
                    }
                }
            });

            currentDate.setDate(currentDate.getDate() + 7);
        }

        return plotBands;
    }

    function diffForHumans(milliseconds : number, parts = 2) {
        let totalSeconds = Math.floor(milliseconds / 1000);
        let components = [];

        let days = Math.floor(totalSeconds / 86400);
        let hours = Math.floor((totalSeconds % 86400) / 3600);
        let minutes = Math.floor((totalSeconds % 3600) / 60);
        let seconds = totalSeconds % 60;

        if (days > 0) components.push(`${days}d`);
        if (hours > 0) components.push(`${hours}h`);
        if (minutes > 0) components.push(`${minutes}m`);
        if (seconds > 0) components.push(`${seconds}s`);

        if (components.length > parts) {
            components = components.slice(0, parts);
        }

        return components.join(' ');
    }

function tables() {
    let table = document.querySelector('.datatable[data-table="tables"]') as HTMLElement;
    if(!table) {
        return;
    }

    let datatable = new DataTable('.datatable[data-table="tables"]',{
        pageLength: 20,
        lengthMenu: [10,20,50,100],
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getTables',
            data: function(data) {
                return data;
            }
        },
        columns: [
            { data: 'table', className: "align-middle" },
            { data: null, className: "align-middle", render: (data) => color(data.size,data.size_color,true,2,false)},
        ],
        order: [[1,"desc"]] as Order[]
    });

    if(document.querySelector('.datatable[data-table="table_groups"]')) {
        new DataTable('.datatable[data-table="table_groups"]',{
            paging: false,
            searching: false,
            info: false,
            order: [[2,"desc"]] as Order[],
        });
    }
}

function source() {
    let chart = document.getElementById('chart_source') as HTMLElement;
    if(!chart) {
        return;
    }

    let series = [] as Highcharts.SeriesOptionsType[];
    let series_data = [] as any;
    Object.values(source_data).forEach((data : any) => {
        series_data.push({
            name: data.name,
            y: data.lines
        });
    });
    series.push({
        name: 'Lines',
        colorByPoint: true,
        data: series_data
    } as Highcharts.SeriesOptionsType);

    Highcharts.chart('chart_source', Highcharts.merge(getChartTheme(), {
        series: series,
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 600,
        },
        title: {
            text: 'Source',
            style: {
                fontSize: '14px'
            }
        },
        credits: {
            enabled: false
        }
    } as any));
}

function source_language() {
    let chart = document.getElementById('chart_source_language') as HTMLElement;
    if(!chart) {
        return;
    }

    let series = [] as Highcharts.SeriesOptionsType[];
    let series_data = [] as any;
    Object.values(source_language_data).forEach((data : any) => {
        series_data.push({
            name: data.name,
            y: data.lines
        });
    });
    series.push({
        name: 'Lines',
        colorByPoint: true,
        data: series_data
    } as Highcharts.SeriesOptionsType);

    Highcharts.chart('chart_source_language', Highcharts.merge(getChartTheme(), {
        series: series,
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 600,
        },
        title: {
            text: 'Language',
            style: {
                fontSize: '14px'
            }
        },
        credits: {
            enabled: false
        }
    } as any));
}

function months() {
    if(typeof data_array !== 'undefined') {
        on('click','.js-month',(target : HTMLElement) => {
            let month = target.getAttribute('name');
            if(!month) {
                return;
            }
            target.classList.add('active');
            let fromInput = document.querySelector('input[name="from"]') as HTMLInputElement;
            let toInput = document.querySelector('input[name="to"]') as HTMLInputElement;
            // D-14/GRAPH-07: key off the currently-displayed range's year (mirrors the
            // server's own $viewYear = date('Y', strtotime($graph_data->from)) derivation,
            // default.latte:27) — NOT wall-clock today, which broke month filters for every
            // past year. Wall-clock fallback only fires when the input is genuinely empty.
            // IN-04: guard the year read — only trust a leading YYYY- on the free-text
            // 'from' input; any other text (empty, partial, garbage) falls back to the
            // current year so this can never build an Invalid-Date/NaN navigation URL.
            let year = fromInput?.value && /^\d{4}-/.test(fromInput.value)
                ? fromInput.value.substring(0, 4)
                : new Date().getFullYear().toString();
            let from_date = year + '-' + month + '-01';
            let lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
            let to_date = year + '-' + month + '-' + lastDay.toString().padStart(2, '0');
            if(fromInput) fromInput.value = from_date;
            if(toInput) toInput.value = to_date;
            setTimeout(() => {
                location.href = '?from=' + from_date + '&to=' + to_date;
            }, 500);
        });
    }
}

function heatmaps() {
    if(typeof heatmap_data === 'undefined') return;

    const year = heatmap_data.year;
    const metrics = ['alcohol', 'runs', 'gym', 'reading', 'weight'] as const;

    for(const metric of metrics) {
        const container = document.getElementById('heatmap_' + metric);
        if(!container) continue;

        const colorLight = container.dataset.colorLight || '#e0e0e0';
        const colorDark = container.dataset.colorDark || '#333333';
        const data = heatmap_data[metric] || {};

        // Find max value for scaling
        const values = Object.values(data) as number[];
        const maxVal = values.length > 0 ? Math.max(...values) : 1;

        // Build grid: 53 weeks x 7 days, fill container width
        const grid = document.createElement('div');
        grid.style.display = 'grid';
        grid.style.gridTemplateColumns = 'repeat(53, 1fr)';
        grid.style.gridTemplateRows = 'repeat(7, 1fr)';
        grid.style.gap = '2px';
        grid.style.width = '100%';
        grid.style.aspectRatio = '53 / 7';

        const startDate = new Date(year, 0, 1);
        const startDow = (startDate.getDay() + 6) % 7; // Monday = 0

        for(let week = 0; week < 53; week++) {
            for(let dow = 0; dow < 7; dow++) {
                const dayOffset = week * 7 + dow - startDow;
                const date = new Date(year, 0, 1 + dayOffset);
                const cell = document.createElement('div');
                cell.style.borderRadius = '2px';
                cell.style.gridColumn = (week + 1).toString();
                cell.style.gridRow = (dow + 1).toString();

                if(date.getFullYear() !== year) {
                    cell.style.backgroundColor = 'transparent';
                } else {
                    const dateStr = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
                    const val = data[dateStr] || 0;

                    if(val === 0) {
                        // CSS custom property resolves live at paint time per theme (Pitfall 7).
                        cell.style.backgroundColor = 'var(--ksat-surface-raised)';
                    } else {
                        const intensity = Math.min(val / maxVal, 1);
                        cell.style.backgroundColor = interpolateColor(colorLight, colorDark, intensity);
                    }

                    cell.dataset.bsToggle = 'tooltip';
                    cell.dataset.bsTitle = dateStr + ': ' + (metric === 'weight' ? (val ? 'Logged' : 'No data') :
                                 metric === 'reading' ? formatSeconds(val) : val);
                }

                grid.appendChild(cell);
            }
        }

        container.appendChild(grid);
    }

    init_tooltips();
}

function interpolateColor(light: string, dark: string, t: number): string {
    const parse = (hex: string) => {
        hex = hex.replace('#', '');
        return [parseInt(hex.slice(0,2),16), parseInt(hex.slice(2,4),16), parseInt(hex.slice(4,6),16)];
    };
    const [r1,g1,b1] = parse(light);
    const [r2,g2,b2] = parse(dark);
    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);
    return `rgb(${r},${g},${b})`;
}

function formatSeconds(secs: number): string {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if(h > 0) return h + 'h ' + m + 'm';
    return m + 'm';
}

